import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ShoppingCart, Globe, DeviceMobile } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import qleanerWebsite from '../../assets/images/qleaner.webp';
import leefPostersImage from '../../assets/images/leef_posters.webp';
import elevateOxygenWebsiteImage from '../../assets/images/elevate_oxygen.webp';

const WebshopPortfolio = () => {
  const { t } = useTranslation();

  const portfolioItems = [
    {
      key: 'webshop1',
      title: 'Elevate Oxygen',
      description: t('webshopPortfolio.project1.description'),
      category: t('webshopPortfolio.project1.category'),
      image: elevateOxygenWebsiteImage,
      technologies: ['React', 'Node.js', 'Stripe'],
      features: ['Responsive Design', 'Payment Integration', 'Admin Dashboard'],
      link: '#'
    },
    {
      key: 'webshop2',
      title: 'Qleaner',
      description: t('webshopPortfolio.project2.description'),
      category: t('webshopPortfolio.project2.category'),
      image: qleanerWebsite,
      technologies: ['Vue.js', 'Laravel', 'PayPal'],
      features: ['Multi-language', 'Inventory Management', 'Analytics'],
      link: '#'
    },
    {
      key: 'webshop3',
      title: 'LEEF. Posters',
      description: t('webshopPortfolio.project3.description'),
      category: t('webshopPortfolio.project3.category'),
      image: leefPostersImage,
      technologies: ['Next.js', 'Shopify', 'Klarna'],
      features: ['SEO Optimized', 'Mobile First', 'Fast Checkout'],
      link: '#'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  const getCategoryIcon = (category) => {
    switch (category.toLowerCase()) {
      case 'ecommerce':
        return ShoppingCart;
      case 'web':
        return Globe;
      case 'mobile':
        return DeviceMobile;
      default:
        return ShoppingCart;
    }
  };

  return (
    <section className="bg-gray-50 py-24">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold font-heading text-gray-900 mb-6">
            {t('webshopPortfolio.title')}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {t('webshopPortfolio.subtitle')}
          </p>
        </div>

        <motion.div
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {portfolioItems.map((item, index) => {
            const CategoryIcon = getCategoryIcon(item.category);
            
            return (
              <motion.div
                key={item.key}
                className="group"
                variants={itemVariants}
              >
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all duration-300 transform hover:-translate-y-1">
                  {/* Image */}
                  <div className="relative overflow-hidden h-48">
                    <img 
                      src={item.image} 
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    
                    {/* Category Badge */}
                    <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-2 text-sm font-medium text-gray-700">
                      <CategoryIcon size={16} weight="thin" />
                      {item.category}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-gray-700 transition-colors duration-300">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      {item.description}
                    </p>

                    {/* Technologies */}
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-2">
                        {item.technologies.map((tech, techIndex) => (
                          <span 
                            key={techIndex}
                            className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full font-medium"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Features */}
                    <div className="mb-6">
                      <ul className="space-y-2">
                        {item.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-center gap-2 text-sm text-gray-600">
                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* CTA */}
                    <div className="flex items-center justify-between">
                      <Link 
                        to="/portfolio"
                        className="inline-flex items-center gap-2 text-gray-700 font-semibold hover:text-gray-900 transition-colors duration-300"
                      >
                        {t('webshopPortfolio.viewProject')}
                        <ArrowRight size={16} weight="thin" />
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        <div className="text-center mt-16">
          <Link 
            to="/portfolio"
            className="inline-flex items-center gap-3 bg-gray-900 text-white px-8 py-4 rounded-full shadow-sm hover:shadow-md transition-all duration-300 transform hover:scale-105"
          >
            <span className="font-semibold">{t('webshopPortfolio.viewAllProjects')}</span>
            <ArrowRight size={20} weight="thin" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default WebshopPortfolio;
